import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';

import CommunityHeader from '@/components/community/CommunityHeader';
import PendingApprovalMessage from '@/components/community/PendingApprovalMessage';
import CommunityTabs from '@/components/community/CommunityTabs';
import StatusRefreshButton from '@/components/community/StatusRefreshButton';
import ProfileEditor from '@/components/community/ProfileEditor';
import ReferralManager from '@/components/community/ReferralManager';

const CommunityPage = () => {
  const { user, isAdmin, isApprovedMember, isLoading: authIsLoading, checkIsAdmin, checkIsApprovedMember } = useAuth();
  const navigate = useNavigate();
  const [stateAmbassadors, setStateAmbassadors] = useState<{[key: string]: number}>({});
  const [memberData, setMemberData] = useState<any>(null);
  const [localLoading, setLocalLoading] = useState(true);
  
  console.log(`[CommunityPage] Rendering. AuthContext state: user=${!!user}, isAdmin=${isAdmin}, isApprovedMember=${isApprovedMember}, authIsLoading=${authIsLoading}. Local loading: ${localLoading}`);

  useEffect(() => {
    console.log('[CommunityPage] User effect triggered. User:', user ? user.id : 'null');
    if (user) {
      fetchUserData();
      fetchAmbassadorCounts();
    } else {
      console.log('[CommunityPage] No user in user effect, setting localLoading to false.');
      setLocalLoading(false); // If no user, we are not loading user-specific data
    }
  }, [user]); // Only user dependency is correct here
  
  const fetchUserData = async () => {
    if (!user) {
      console.log('[CommunityPage] fetchUserData called but no user.');
      if (localLoading) setLocalLoading(false);
      return;
    }
    
    console.log('[CommunityPage] fetchUserData started for user:', user.id);
    try {
      if(!localLoading) setLocalLoading(true);
      const { data, error } = await supabase
        .from('community_members')
        .select('*')
        .eq('id', user.id)
        .single();
      
      if (error) throw error;
      console.log('[CommunityPage] fetchUserData success. Data:', data);
      setMemberData(data);
    } catch (error) {
      console.error('[CommunityPage] Error fetching user data:', error);
    } finally {
      console.log('[CommunityPage] fetchUserData finished. Setting localLoading to false.');
      setLocalLoading(false);
    }
  };
  
  const fetchAmbassadorCounts = async () => {
    console.log('[CommunityPage] fetchAmbassadorCounts started.');
    try {
      const { data, error } = await supabase
        .from('community_members')
        .select('state, id')
        .eq('approved', true);
      
      if (error) throw error;
      
      if (data) {
        const counts: {[key: string]: number} = {};
        data.forEach(member => {
          counts[member.state] = (counts[member.state] || 0) + 1;
        });
        
        const ambassadorCounts: {[key: string]: number} = {};
        Object.keys(counts).forEach(state => {
          ambassadorCounts[state] = Math.floor(counts[state] * 0.2); 
        });
        
        console.log('[CommunityPage] fetchAmbassadorCounts success. Counts:', ambassadorCounts);
        setStateAmbassadors(ambassadorCounts);
      }
    } catch (error) {
      console.error('[CommunityPage] Error fetching ambassador counts:', error);
    }
  };
  
  useEffect(() => {
    console.log(`[CommunityPage] Auth effect triggered. authIsLoading: ${authIsLoading}, User: ${!!user}`);
    if (!authIsLoading && !user) {
      console.log('[CommunityPage] Not authenticated and auth not loading. Navigating to /join.');
      navigate('/join');
    } else if (!authIsLoading && user) {
      console.log('[CommunityPage] Authenticated and auth not loading. Re-checking roles and showing welcome toast.');
      checkIsAdmin(); 
      checkIsApprovedMember();
    }
  }, [authIsLoading, user, navigate, checkIsAdmin, checkIsApprovedMember]);
  
  if (authIsLoading || localLoading) {
    console.log(`[CommunityPage] Showing loading indicator: authIsLoading=${authIsLoading}, localLoading=${localLoading}`);
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-omoduduwa-terracotta border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-lg text-white">Loading community...</p>
        </div>
      </div>
    );
  }
  
  if (!user) {
    console.log('[CommunityPage] Render: No user found (should be redirected by effect). Returning null.');
    return null;
  }
  
  if (!isApprovedMember && !isAdmin) {
    console.log(`[CommunityPage] Render: User is not approved member (isApprovedMember=${isApprovedMember}) AND not admin (isAdmin=${isAdmin}). Showing PendingApprovalMessage.`);
    return <PendingApprovalMessage />;
  }
  
  console.log('[CommunityPage] Render: Rendering main community content.');
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      <CommunityHeader />
      
      <div className="container-custom py-8">
        <CommunityTabs isAdmin={isAdmin} stateAmbassadors={stateAmbassadors} />
        
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 mt-8">
          <div className="md:col-span-12 space-y-6">
            {memberData && (
              <ProfileEditor 
                memberData={memberData} 
                onUpdate={fetchUserData}
              />
            )}
            <ReferralManager />
          </div>
        </div>

        {user && (
          <StatusRefreshButton 
            isAdmin={isAdmin} 
            isApprovedMember={isApprovedMember}
            checkIsAdmin={checkIsAdmin}
            checkIsApprovedMember={checkIsApprovedMember}
          />
        )}
      </div>
    </div>
  );
}

export default CommunityPage;
